import { useCallback, useEffect, useRef } from 'react';

/**
 * AK-47: страховка от «зависшего» интерфейса после закрытия вложенных модалов.
 *
 * Radix (react-remove-scroll) вешает на <body> инлайновый `pointer-events: none`,
 * пока открыт модальный слой. Если два вложенных слоя (Dialog + AlertDialog)
 * размонтируются в один тик, снятие стиля иногда теряется — картинка живая,
 * но клики не проходят. Для администратора это выглядит как зависание программы.
 *
 * Хук:
 *  1) наблюдает за атрибутом style у <body>;
 *  2) если блокировка держится дольше `graceMs` и при этом ни одного открытого
 *     Radix-слоя нет — пишет диагностику и снимает блокировку.
 */
const RADIX_LAYER_SELECTOR = '[data-radix-popper-content-wrapper],[role="dialog"],[role="alertdialog"]';

export function useModalInteractionGuard(options?: { graceMs?: number; label?: string }) {
  const graceMs = options?.graceMs ?? 1500;
  const label = options?.label ?? 'modal';
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const isBodyLocked = () =>
    typeof document !== 'undefined' && document.body.style.pointerEvents === 'none';

  const hasOpenLayer = () =>
    typeof document !== 'undefined' &&
    document.querySelectorAll(RADIX_LAYER_SELECTOR).length > 0;

  const check = useCallback(() => {
    if (!isBodyLocked()) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (!isBodyLocked()) return;
      if (hasOpenLayer()) return; // слой реально открыт — блокировка законна
      console.warn(
        `🛟 [ModalGuard] Снята залипшая блокировка кликов после закрытия «${label}». ` +
          'Открытых модальных слоёв нет, но body был заблокирован дольше нормы.'
      );
      document.body.style.removeProperty('pointer-events');
    }, graceMs);
  }, [clearTimer, graceMs, label]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(check);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    return () => {
      observer.disconnect();
      clearTimer();
    };
  }, [check, clearTimer]);

  /** Вызывать сразу после программного закрытия модала. */
  return { armGuard: check };
}
